// Deterministic seeded shuffle for MCQ-style options — the web mirror of iOS
// `MCQOptionShuffle.swift` (`stableShuffled(seed:)`).
//
// Why this exists: the question-generation LLM (Gemini) has a positional bias —
// it disproportionately makes the FIRST option ("A") correct. Even with explicit
// prompt rules the bias creeps back. iOS already neutralises it by shuffling the
// option order at render time; the web renderer did not, so correct answers
// looked "always A" in the browser. We shuffle here too.
//
// The shuffle is keyed by a string seed (the item id). Same seed → same
// permutation across re-renders and re-encounters, so there's no flicker and
// grading is unaffected: each option keeps its `id`, and the renderer keys
// correctness on `option.id`, not position. Only the DISPLAY order (and the
// remapped A/B/C/D labels) change.

const U64 = (1n << 64n) - 1n;

/** FNV-1a 64-bit hash of the seed's UTF-8 bytes. Process-stable (unlike JS's
 *  built-in string hashing), mirroring the iOS `SeededRNG` initialiser. */
function fnv1a64(seed: string): bigint {
  let h = 0xcbf29ce484222325n;
  for (const b of new TextEncoder().encode(seed)) {
    h ^= BigInt(b);
    h = (h * 0x100000001b3n) & U64;
  }
  return h === 0n ? 0x9e3779b97f4a7c15n : h;
}

/** SplitMix64-style seedable PRNG — tiny, deterministic, good enough for
 *  cosmetic option shuffling. Matches `MCQOptionShuffle.SeededRNG.next()`. */
class SeededRng {
  private state: bigint;
  constructor(seed: string) {
    this.state = fnv1a64(seed);
  }
  next(): bigint {
    this.state = (this.state + 0x9e3779b97f4a7c15n) & U64;
    let z = this.state;
    z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & U64;
    z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & U64;
    return (z ^ (z >> 31n)) & U64;
  }
}

/** Returns `items` shuffled deterministically by `seed` (Fisher–Yates). Same
 *  seed → same permutation. */
export function stableShuffle<T>(items: T[], seed: string): T[] {
  const rng = new SeededRng(seed);
  const a = items.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Number(rng.next() % BigInt(i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
